/**
 * The one HTTP client for `/api/scan-print/me/*`: the signed-in member's
 * short-lived My QR token. Contract: church-systems-docs
 * `HOGC One/Scan to Print/README.md`.
 *
 * URLs are built from `VUE_APP_API_URL` at call time, like
 * `courses-encryption-api.js`, so the global interceptors (falsy-bearer
 * repair, 401 refresh) apply. Every failure becomes a `ScanPrintError` with a
 * `code` My QR can branch on.
 */
import axios from 'axios';

export const SCAN_PRINT_ERRORS = Object.freeze({
  /** 404: the flag is off for this member on the server. Show the old QR. */
  UNAVAILABLE: 'UNAVAILABLE',
  RATE_LIMITED: 'RATE_LIMITED',
  /** Offline, a dropped request, a 5xx: retry shortly. */
  FAILED: 'FAILED',
});

export class ScanPrintError extends Error {
  constructor(code, cause) {
    super(`Scan to Print: ${code}`);
    this.name = 'ScanPrintError';
    this.code = code;
    this.status = cause?.response?.status ?? null;
  }
}

function toError(error) {
  const status = error?.response?.status;
  if (status === 404) return new ScanPrintError(SCAN_PRINT_ERRORS.UNAVAILABLE, error);
  if (status === 429) return new ScanPrintError(SCAN_PRINT_ERRORS.RATE_LIMITED, error);
  return new ScanPrintError(SCAN_PRINT_ERRORS.FAILED, error);
}

/**
 * A fresh My QR token, valid for 60 seconds (any number of scans until then).
 * @returns {Promise<{token: string, expiresAt: string, refreshAfterSeconds: number}>}
 */
export async function fetchQrToken({ token }) {
  let response;
  try {
    response = await axios.get(`${process.env.VUE_APP_API_URL}/scan-print/me/qr-token`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (error) {
    throw toError(error);
  }
  if (!response?.data?.token) throw new ScanPrintError(SCAN_PRINT_ERRORS.FAILED);
  return response.data;
}
